import type { Quote } from "@shared/schema";
import { QuotesList, NewQuoteProcess } from "./quoting/index";
import { RfpEmailSetupRequired } from "./quoting/RfpEmailSetupRequired";
import { useQueryStates, parseAsInteger, parseAsStringLiteral } from "nuqs";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";

export type WizardMode = "quote" | "rfp";

export default function Quoting() {
  const { hasFeature } = useSubscriptionTier();
  const canUseRfp = hasFeature("advanced_quoting");

  // RFPs go out from the agency's provisioned sending address. There is no
  // fallback — we never send an agency's mail from an Administr address.
  const { data: emailSettings, isLoading: emailSettingsLoading } = useQuery<{
    fromEmail: string | null;
    provisioningStatus: string | null;
    isActive: boolean | null;
  }>({ queryKey: ["/api/agency/email-settings"] });
  const canSendEmail =
    !!emailSettings?.fromEmail &&
    !!emailSettings.isActive &&
    emailSettings.provisioningStatus === "provisioned";
  const [queryParams, setQueryParams] = useQueryStates(
    {
      wizard: parseAsStringLiteral(["true", ""] as const).withDefault(""),
      mode: parseAsStringLiteral(["quote", "rfp"] as const).withDefault(
        "quote"
      ),
      step: parseAsInteger.withDefault(1),
      draft: parseAsInteger,
      client: parseAsInteger,
      prospect: parseAsInteger,
    },
    {
      clearOnDefault: false,
    }
  );

  const {
    wizard,
    mode: wizardMode,
    step: urlStep,
    draft: draftId,
    client: preselectedClientId,
    prospect: preselectedProspectId,
  } = queryParams;

  useEffect(() => {
    if (wizard && wizardMode === "rfp" && !canUseRfp) {
      setQueryParams({ mode: "quote" });
    }
  }, [wizard, wizardMode, canUseRfp, setQueryParams]);

  // Fetch draft quote if draftId is in URL
  const { data: draftQuote, isLoading: isDraftLoading } =
    useQuery<Quote | null>({
      queryKey: ["draft-quote", draftId],
      queryFn: async () => {
        if (!draftId) return null;
        const res = await fetch(`/api/quotes/${draftId}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to fetch draft");
        return res.json();
      },
      enabled: !!draftId && !!wizard,
      staleTime: 0,
    });

  const handleNewQuote = () => {
    setQueryParams(
      { wizard: "true", mode: "quote", step: 1, draft: null },
      { history: "push" }
    );
  };

  const handleNewRFP = () => {
    setQueryParams(
      { wizard: "true", mode: "rfp", step: 1, draft: null },
      { history: "push" }
    );
  };

  const handleEditQuote = (quote: Quote) => {
    const savedStep = (quote as any).currentStep || 1;
    const ws = (quote as any).wizardState;
    const savedMode = ws?.mode === "rfp" ? "rfp" : "quote";
    setQueryParams(
      {
        wizard: "true",
        mode: savedMode,
        step: savedStep,
        draft: quote.id,
      },
      { history: "push" }
    );
  };

  const handleCloseQuoteProcess = () => {
    setQueryParams({ wizard: null, mode: null, step: null, draft: null, client: null, prospect: null });
  };

  const handleDraftIdChange = (id: number | null) => {
    setQueryParams({ draft: id });
  };

  const handleStepChange = (step: number) => {
    setQueryParams({ step });
  };

  if (wizard) {
    // Checked at the door, not on the final step: an RFP is only worth building
    // if it can be emailed, and that needs the agency's own sending address.
    if (wizardMode === "rfp" && !emailSettingsLoading && !canSendEmail) {
      return <RfpEmailSetupRequired onBack={handleCloseQuoteProcess} />;
    }

    // Show loading only when resuming an existing draft that hasn't loaded yet
    if (draftId && isDraftLoading) {
      return (
        <div className="flex items-center justify-center h-[calc(100vh-180px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading draft...</p>
          </div>
        </div>
      );
    }

    return (
      <>
        <NewQuoteProcess
          onClose={handleCloseQuoteProcess}
          existingQuote={draftId ? draftQuote : null}
          mode={wizardMode}
          initialStep={urlStep}
          initialClientId={preselectedClientId}
          initialProspectId={preselectedProspectId}
          onStepChange={handleStepChange}
          onDraftIdChange={handleDraftIdChange}
        />
      </>
    );
  }

  return (
    <QuotesList
      onNewQuote={handleNewQuote}
      onNewRFP={handleNewRFP}
      onEditQuote={handleEditQuote}
      canCreateRfp={canUseRfp}
    />
  );
}
